import {AfterViewInit, Component, HostListener, Inject, OnInit, ViewChild} from '@angular/core';
import {MatSelectChange} from '@angular/material/select';
import {MatSidenav} from '@angular/material/sidenav';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {DrawingImageUploadHelper} from '@application/helper/drawing/drawing-image-upload-helper';
import {isClickOutsideGivenElements} from '@application/helper/is-click-outside-given-elements';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {DrawingLibraryViewMode} from '@domain/drawing-library/drawing-library-view-mode.enum';
import {EventExecutedSuccessfullyUpdate} from '@domain/event-executed-successfully-update';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {SIGNALR_EVENT, SignalrEvent} from '@infrastructure/signalr/events/signalr-event.interface';
import {RealtimeEvent} from '@infrastructure/signalr/realtime-event.enum';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {AdvancedSearchInput} from '@presentation/components/search-filters/advanced-search/advanced-search-input.enum';
import {AdvancedSearchDialogComponent} from '@presentation/components/search-filters/advanced-search/dialog/advanced-search-dialog.component';
import {SearchFiltersComponent} from '@presentation/components/search-filters/search-filters.component';
import {DrawingLibraryDetailsComponent} from '@presentation/pages/texedit/drawing-library/drawing-library-details/drawing-library-details.component';
import {DrawingLibraryThumbnailsComponent} from '@presentation/pages/texedit/drawing-library/drawing-library-thumbnails/drawing-library-thumbnails.component';
import {
  AlertDialogResult,
  AssertionUtils,
  BaseComponent,
  DesignFileType,
  DialogBuilderFactoryService,
  DialogType,
  EnumUtils,
  FilterComponent,
  ObjectUtils,
  ToastService,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {cloneDeep} from 'lodash-es';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {NavigationDrawingData} from './drawing-library-details/navigation-drawing-data.interface';
import {DrawingLibraryListViewComponent} from './drawing-library-new-listview/drawing-library-listview.component';

@Component({
  templateUrl: './drawing-library.component.html',
  styleUrls: ['./drawing-library.component.scss']
})
export class DrawingLibraryComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('drawingLibraryDetailsComponent') public drawingLibraryDetailsComponent: DrawingLibraryDetailsComponent;
  @ViewChild('drawingLibraryThumbnailsComponent') public drawingLibraryThumbnailsComponent: DrawingLibraryThumbnailsComponent;
  @ViewChild('drawingLibraryNewListViewComponent') public drawingLibraryNewListViewComponent: DrawingLibraryListViewComponent;
  @ViewChild('sidenav', {static: true}) public sidenav: MatSidenav;
  @ViewChild('nameFilter') public nameFilter: FilterComponent;
  @ViewChild('searchFilters') public searchFilters: SearchFiltersComponent;

  public readonly urlToNavigateToSettings = RouteUtils.paths.texEdit.designLibrarySettings.absolutePath;
  public readonly allowedFileExtensionForFiles = [DesignFileType.BMP, DesignFileType.EP, DesignFileType.TFT, DesignFileType.PVD].join();
  public readonly TEXEDIT_EDIT_DRAWING_LIBRARY = Permission.TEXEDIT_EDIT_DRAWING_LIBRARY;
  public readonly TEXSTYLE_VIEW = Permission.TEXSTYLE_VIEW;
  public readonly viewModes: DrawingLibraryViewMode[] = EnumUtils.getEnumValues(DrawingLibraryViewMode);

  public selectedDrawing: ListDrawingConfiguration;
  public selectedDrawingLibraryViewMode: DrawingLibraryViewMode = DrawingLibraryViewMode.THUMBNAILS;
  public advancedSearchFilters: PropertyValue[] = [];
  public selectMultipleButtonActive = false;
  public selectedDrawingId = null;
  public selectedDrawingState: Drawing;
  public defaultUnit = Unit.PIXEL;
  public isExporting = false;
  public hasEditPermission: boolean;
  public hasSettingsPermission: boolean;

  private currentDrawing: Drawing;
  private newDrawingImageId: string = null;
  private readonly nameFilterChangeSubject = new Subject<string>();
  private hasDrawingBeenSaved = false;
  private readonly listOfClassesThatShouldNotCloseSidebarOnClick = ['drawing-container', 'drawing-detail-container', 'cdk-overlay-container', 'toast-container', 'ag-row', 'delete'];
  private drawingsToDelete: ListDrawingConfiguration[];
  private designTranslation = 'DESIGN_LIBRARY.DESIGN';
  private isDeleting = false;
  private currentSubscription: Subscription;
  private canSeeBackIcon: boolean;

  public constructor(
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    @Inject(SIGNALR_EVENT) private readonly signalrEvent: SignalrEvent,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly drawingImageUploadHelper: DrawingImageUploadHelper,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHelperService: NavigationHelperService<NavigationDrawingData>
  ) {
    super();
    this.drawingImageUploadHelper.unSubscribeOnViewDestroy = this.unSubscribeOnViewDestroy;
  }

  @HostListener('document:click', ['$event'])
  public onOutsideDetailClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    if (this.sidenav.opened && isClickOutsideGivenElements(targetElement, this.listOfClassesThatShouldNotCloseSidebarOnClick)) {
      this.onDrawingCanceled();
    }
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.hasEditPermission = this.currentSubscription?.hasPermission(this.TEXEDIT_EDIT_DRAWING_LIBRARY);
    this.hasSettingsPermission = this.currentSubscription?.hasPermission(this.TEXSTYLE_VIEW);

    this.canSeeBackIcon = !history?.state?.isRedirect ?? true;

    this.drawings.clearUploadQueue();

    this.sidenav.closedStart.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.hasDrawingBeenSaved = false;
    });

    this.subscribeToNameFilterChanges();
    this.initialiseAdvancedSearchFilters();
    this.initialiseSelectedDrawing();
  }

  public ngAfterViewInit(): void {
    this.getSaveEpEventExecutedSuccessfully();
  }

  public isModeThumbnails(): boolean {
    return this.selectedDrawingLibraryViewMode === DrawingLibraryViewMode.THUMBNAILS || this.selectedDrawingLibraryViewMode === DrawingLibraryViewMode.THUMBNAILS_WITH_CONFIGURATIONS;
  }

  public isModeTable(): boolean {
    return this.selectedDrawingLibraryViewMode === DrawingLibraryViewMode.TABLE;
  }

  public onDrawingFileChange(files: File[]): void {
    this.drawingImageUploadHelper.uploadFiles(files);
  }

  public navigateToPage(pageUrl: string): void {
    this.router.navigateByUrl(pageUrl);
  }

  public onDrawingReplaced(drawingImages: DrawingImage[]): void {
    this.currentDrawing = cloneDeep(this.selectedDrawing.drawing);

    if (drawingImages?.length > 0) {
      const uploadedImage = drawingImages[0];
      this.selectedDrawing.drawing.image = uploadedImage;
      this.newDrawingImageId = uploadedImage.id;
      this.drawingLibraryDetailsComponent.changeDrawingImage(uploadedImage);
    }

    this.drawingLibraryDetailsComponent.hideLoadingView();
  }

  public onDrawingSelected(selectedDrawings: ListDrawingConfiguration[], hasDrawingState: boolean = false): void {
    if (!hasDrawingState) {
      this.selectedDrawingState = null;
    }

    if (this.selectMultipleButtonActive && !this.isModeTable()) {
      this.selectMultiple(selectedDrawings);
    } else {
      this.selectSingle(selectedDrawings[0]);
      this.selectMultiple(selectedDrawings);
    }
  }

  public onDrawingDeleted(deletedDrawings: Drawing[]): void {
    this.selectedDrawing = null;
    this.drawingsToDelete = [];
    this.isDeleting = false;
    this.deleteNewDrawingImageIfNeeded();

    if (this.isModeThumbnails()) {
      this.drawingLibraryThumbnailsComponent.onDrawingDeleted(deletedDrawings);
    } else if (this.isModeTable()) {
      this.drawingLibraryNewListViewComponent.onDrawingDeleted(deletedDrawings);
    }

    if (!AssertionUtils.isNullOrUndefined(this.sidenav) && this.sidenav.opened) {
      this.sidenav.close();
    }
  }

  public onDrawingCanceled(): void {
    this.deleteNewDrawingImageIfNeeded();

    if (this.isModeThumbnails()) {
      this.drawingLibraryThumbnailsComponent.onDrawingCanceled();
    } else if (this.isModeTable()) {
      this.drawingLibraryNewListViewComponent.onDrawingCanceled();
    }

    if (!AssertionUtils.isNullOrUndefined(this.sidenav) && this.sidenav.opened) {
      this.sidenav.close();
    }

    if (this.shouldResetDrawingToOriginalState()) {
      this.selectedDrawing.drawing.image = this.currentDrawing.image;
    }

    this.currentDrawing = null;
    this.selectedDrawing = null;
  }

  public onDrawingUpdated(updatedDrawing: Drawing): void {
    this.selectedDrawing.drawing = updatedDrawing;
    this.newDrawingImageId = null;

    if (this.isModeThumbnails()) {
      this.drawingLibraryThumbnailsComponent.onDrawingUpdated(updatedDrawing);
    } else if (this.isModeTable()) {
      this.drawingLibraryNewListViewComponent.onDrawingUpdated(updatedDrawing);
    }

    this.hasDrawingBeenSaved = true;
  }

  public onNameFilterChange(filterText: string): void {
    this.nameFilterChangeSubject.next(filterText);
  }

  public exportComplete(): void {
    this.isExporting = false;
  }

  public showAdvancedSearch(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass(['reposition-dialog', 'drawing-library-advanced-search'])
      .withWidth('800px')
      .withMaxHeight('100%')
      .openDialog(RepositionDialogComponent, {
        advancedSearchFilters: cloneDeep(this.advancedSearchFilters),
        defaultUnit: this.defaultUnit,
        component: AdvancedSearchDialogComponent,
        sourceElement: this.nameFilter.iconElement.nativeElement,
        positionOfDialog: PositionOfDialog.TOP
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((advancedSearchFilters: PropertyValue[]) => {
        this.updateAdvancedSearchFilters(advancedSearchFilters);
      });
  }

  public updateFilteredDrawings(): void {
    if (!AssertionUtils.isNullOrUndefined(this.nameFilter)) {
      this.nameFilter.filteredText = this.advancedSearchFilters.find((value: PropertyValue) => value.propertyName === AdvancedSearchInput.NAME)?.propertyValue;
    }

    this.reloadDrawings();
  }

  public onViewModeFilterChange(matSelectChange: MatSelectChange): void {
    this.selectedDrawingLibraryViewMode = matSelectChange.value;
    this.selectedDrawing = null;
    this.deleteNewDrawingImageIfNeeded();
  }

  public hasDrawingsToBeDeleted(): boolean {
    return !AssertionUtils.isEmpty(this.drawingsToDelete) && !this.isDeleting;
  }

  public removeConfirmation(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {
          object: this.drawingsToDelete.length > 1 ? this.translate.instant(this.designTranslation, {count: 2}).toLowerCase() : this.drawingsToDelete[0].drawing.name
        }),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (result === AlertDialogResult.CONFIRM) {
          this.deleteDrawing(this.drawingsToDelete);
        }
      });
  }

  public toggleSelectMultipleButton(): void {
    this.drawingsToDelete = [];
    this.selectedDrawing = null;
    if (this.isModeThumbnails()) {
      this.drawingLibraryThumbnailsComponent.selectedDrawings = [];
    } else {
      this.drawingLibraryNewListViewComponent.drawingsGrid.api.deselectAll();
    }

    this.selectMultipleButtonActive = !this.selectMultipleButtonActive;
  }

  public updateAdvancedSearchFilters(advancedSearchFilters: PropertyValue[]): void {
    if (advancedSearchFilters?.length > 0) {
      this.advancedSearchFilters = advancedSearchFilters;
      if (!AssertionUtils.isNullOrUndefined(this.nameFilter)) {
        this.nameFilter.filteredText = this.advancedSearchFilters.find((value: PropertyValue) => value.propertyName === AdvancedSearchInput.NAME)?.propertyValue;
      }
      if (this.searchFilters) {
        this.searchFilters.emitCheckIfFiltersAreTooBigForContainerEvent();
      }
      this.reloadDrawings();
    }
  }

  public canUseCustomFilterComponent(): boolean {
    return !this.isModeTable();
  }

  public shouldShowBackButton(): boolean {
    return this.canSeeBackIcon;
  }

  private initialiseSelectedDrawing(): void {
    const emptyDrawingState = {drawing: null} as NavigationDrawingData;
    const drawingState = this.navigationHelperService.getPartialState<NavigationDrawingData>(Object.keys(emptyDrawingState));

    if (!AssertionUtils.isNullOrUndefined(drawingState) && !ObjectUtils.isDeepEqual(drawingState, emptyDrawingState)) {
      this.selectedDrawingId = drawingState.drawing.id;
      this.selectedDrawingState = drawingState.drawing;
      this.selectDrawing(drawingState.drawing, true);
    } else {
      this.activatedRoute.queryParams.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((queryParams: Params) => {
        if (queryParams.id) {
          this.selectedDrawingId = Number(queryParams.id);

          this.drawings
            .getById(this.selectedDrawingId)
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe((drawing: Drawing) => this.selectDrawing(drawing));

          this.router.navigate([], {relativeTo: this.activatedRoute, queryParams: null});
        }
      });
    }
  }

  private selectDrawing(drawing: Drawing, hasDrawingState: boolean = false): void {
    const drawingWrappers = this.isModeThumbnails() ? ListDrawingConfiguration.getDrawingsForAllConfigurations([drawing]) : [new ListDrawingConfiguration(drawing, 0)];
    this.onDrawingSelected(drawingWrappers, hasDrawingState);
  }

  private deleteDrawing(drawingsToDelete: ListDrawingConfiguration[]): void {
    this.isDeleting = true;
    const inUse = drawingsToDelete.filter((drawingConfiguration: ListDrawingConfiguration) => drawingConfiguration.drawing.used);
    const drawingsThatCanBeDeleted = drawingsToDelete.filter((drawingConfiguration: ListDrawingConfiguration) => !drawingConfiguration.drawing.used);

    const idsOfDrawingsThatCanBeDeleted = drawingsThatCanBeDeleted.map((drawingThatCanBeDeleted: ListDrawingConfiguration) => drawingThatCanBeDeleted.drawing.id);
    this.drawings
      .deleteMultiple(idsOfDrawingsThatCanBeDeleted)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((idsOfDrawingsThatAreDeleted: number[]) => this.drawingsDeleted(idsOfDrawingsThatCanBeDeleted, idsOfDrawingsThatAreDeleted));

    if (!AssertionUtils.isEmpty(inUse)) {
      this.toastService.showError({tapToDismiss: false, timeOut: 20000, message: this.getNotBeingAbleToDeleteMessage(inUse)});
    }
  }

  private drawingsDeleted(idsOfDrawingsThatCanBeDeleted: number[], idsOfDrawingsThatAreDeleted: number[]): void {
    const amountThatCanBeDeleted = idsOfDrawingsThatCanBeDeleted?.length ?? 0;
    const amountThatIsDeleted = idsOfDrawingsThatAreDeleted?.length ?? 0;

    const params = {tapToDismiss: false, timeOut: 2000, message: ''};

    if (amountThatCanBeDeleted !== amountThatIsDeleted) {
      const amountThatIsNotDeleted = amountThatCanBeDeleted - amountThatIsDeleted;
      params.message = this.translate.instant('GENERAL.ACTIONS.NOT_DELETED_OBJECT_WITH_AMOUNT', {
        amount: amountThatIsNotDeleted,
        object: this.translate.instant(this.designTranslation, {count: amountThatIsDeleted > 1 ? 2 : 1}).toLowerCase()
      });
      this.toastService.showError(params);
    } else {
      params.message = this.translate.instant('GENERAL.ACTIONS.DELETED_OBJECT_WITH_AMOUNT', {
        amount: amountThatIsDeleted,
        object: this.translate.instant(this.designTranslation, {count: amountThatIsDeleted > 1 ? 2 : 1}).toLowerCase()
      });
      this.toastService.showInfo(params);
    }

    const drawingConfigsThatAreDeleted = this.drawingsToDelete?.filter((drawingConfiguration: ListDrawingConfiguration) => idsOfDrawingsThatAreDeleted?.includes(drawingConfiguration.drawing.id));
    const drawingsThatAreDeleted = drawingConfigsThatAreDeleted?.map((drawingConfiguration: ListDrawingConfiguration) => drawingConfiguration.drawing);
    this.onDrawingDeleted(drawingsThatAreDeleted);
  }

  private getNotBeingAbleToDeleteMessage(inUseDrawings: ListDrawingConfiguration[]): string {
    if (inUseDrawings.length > 1) {
      const message = inUseDrawings.map((config: ListDrawingConfiguration) => config.drawing.name).join(', ');
      return this.translate.instant('DESIGN_LIBRARY.ERRORS.CANNOT_BE_DELETED_MULTIPLE', {names: message}).toLowerCase();
    }

    return this.translate.instant('DESIGN_LIBRARY.ERRORS.CANNOT_BE_DELETED', {name: inUseDrawings[0].drawing.name}).toLowerCase();
  }

  private getConfirmationDeleteMessage(): string {
    if (this.drawingsToDelete.length === 1) {
      return this.translate.instant(this.designTranslation, {count: 1}).toLowerCase();
    }

    return `${this.drawingsToDelete.length} ${this.translate.instant('DESIGN_LIBRARY.DESIGN', {count: 2}).toLowerCase()}`;
  }

  private deleteNewDrawingImageIfNeeded(): void {
    if (!AssertionUtils.isNullOrUndefined(this.newDrawingImageId)) {
      this.drawings.deleteDrawingImages([this.newDrawingImageId]).subscribe();
      this.newDrawingImageId = null;
    }
  }

  private subscribeToNameFilterChanges(): void {
    this.nameFilterChangeSubject
      .asObservable()
      .pipe(debounceTime(500), takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((filterText: string) => {
        this.advancedSearchFilters.find((value: PropertyValue) => value.propertyName === AdvancedSearchInput.NAME).propertyValue = filterText;
        this.searchFilters.emitCheckIfFiltersAreTooBigForContainerEvent();
        this.reloadDrawings();
      });
  }

  private initialiseAdvancedSearchFilters(): void {
    const typeFilter: PropertyValue = {
      propertyName: AdvancedSearchInput.DRAWING_TYPE,
      possiblePropertyValues: [DrawingType[DrawingType.BMP], DrawingType[DrawingType.EP], DrawingType[DrawingType.TFT]]
    };
    const shapeFilter: PropertyValue = {propertyName: AdvancedSearchInput.DRAWING_SHAPE};
    this.advancedSearchFilters.push(
      {propertyName: AdvancedSearchInput.NAME},
      {propertyName: AdvancedSearchInput.DRAWING_CODE},
      shapeFilter,
      typeFilter,
      {propertyName: AdvancedSearchInput.MACHINE_QUALITY},
      {propertyName: AdvancedSearchInput.COLOR_SET},
      {propertyName: AdvancedSearchInput.MAIN_COLOR},
      {propertyName: AdvancedSearchInput.BORDER_COLOR},
      {propertyName: AdvancedSearchInput.HEIGHT_IN_PICKS},
      {propertyName: AdvancedSearchInput.WIDTH_IN_DENTS}
    );
    this.drawings
      .getDrawingShapes()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((drawingShapes: string[]) => {
        if (!AssertionUtils.isNullOrUndefined(drawingShapes)) {
          shapeFilter.possiblePropertyValues = drawingShapes;
        }
      });
  }

  private shouldResetDrawingToOriginalState(): boolean {
    return (
      !AssertionUtils.isNullOrUndefined(this.currentDrawing) &&
      !AssertionUtils.isNullOrUndefined(this.selectedDrawing) &&
      !this.hasDrawingBeenSaved &&
      this.currentDrawing.image.id !== this.selectedDrawing.drawing.image.id
    );
  }

  private reloadDrawings(): void {
    if (this.isModeThumbnails()) {
      this.drawingLibraryThumbnailsComponent.reloadDrawings(this.advancedSearchFilters);
    }
  }

  private getSaveEpEventExecutedSuccessfully(): void {
    this.signalrEvent
      .getEventExecutedSuccessfullyUpdates()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((eventExecutedSuccessfullyUpdate: EventExecutedSuccessfullyUpdate) => {
        if (
          !AssertionUtils.isNullOrUndefined(eventExecutedSuccessfullyUpdate) &&
          (eventExecutedSuccessfullyUpdate.eventName === RealtimeEvent.EP_SAVED || eventExecutedSuccessfullyUpdate.eventName === RealtimeEvent.PROCESSING_FAILED)
        ) {
          this.toastService.showInfo({
            tapToDismiss: false,
            timeOut: 2000,
            message: this.translate.instant(
              eventExecutedSuccessfullyUpdate.eventName === RealtimeEvent.EP_SAVED
                ? 'DESIGN_LIBRARY.DETAILS.CONFIGURATION.EP_SAVED_SUCCESFULLY'
                : 'DESIGN_LIBRARY.DETAILS.CONFIGURATION.EP_PROCESSING_FAILED'
            )
          });

          if (eventExecutedSuccessfullyUpdate.eventName === RealtimeEvent.EP_SAVED) {
            this.reloadDrawings();
          }
        }
      });
  }

  private selectMultiple(selectedDrawings: ListDrawingConfiguration[]): void {
    this.drawingsToDelete = selectedDrawings;
  }

  private selectSingle(selectedDrawing: ListDrawingConfiguration): void {
    if (this.shouldResetDrawingToOriginalState()) {
      this.selectedDrawing.drawing.image = this.currentDrawing.image;
    }

    this.selectedDrawing = selectedDrawing;
    this.newDrawingImageId = null;
    this.toggleSideNav();
  }

  private toggleSideNav(): void {
    const sidenavShouldBeOpen = !AssertionUtils.isNullOrUndefined(this.selectedDrawing);

    if (sidenavShouldBeOpen) {
      if (!AssertionUtils.isNullOrUndefined(this.sidenav) && !this.sidenav.opened) {
        this.sidenav.open();
      }
    } else {
      if (!AssertionUtils.isNullOrUndefined(this.sidenav) && this.sidenav.opened) {
        this.sidenav.close();
      }
    }
  }
}
